import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { Download, Loader2, ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { productAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

// Helper to get localized text from product fields
const getLocalizedText = (field, lang) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field[lang] || field['en'] || field['no'] || '';
};

    // Helper to get localized array from product fields
    const getLocalizedArray = (field, lang) => {
        if (!field) return [];
        if (Array.isArray(field)) return field;
        return field[lang] || field['en'] || field['no'] || [];
    };

    // Get top 3 minerals by actual value (highest concentration) - sorted by numeric value
    // Always ensure magnesium is included as 4th mineral if not in top 3
    const getTopMinerals = (mineralContent) => {
        if (!mineralContent) return [];
        const minerals = ['magnesium', 'calcium', 'sodium', 'bicarbonate', 'potassium', 'sulfate', 'chloride', 'silica'];
        const mineralValues = minerals
            .filter(m => mineralContent[m])
            .map(key => ({
                key,
                value: mineralContent[key],
                numericValue: parseFloat(mineralContent[key].replace(/[^\d.]/g, '')) || 0
            }))
            .sort((a, b) => b.numericValue - a.numericValue);
        
        // Get top 3
        const top3 = mineralValues.slice(0, 3);
        
        // Check if magnesium is already in top 3
        const hasMagnesium = top3.some(m => m.key === 'magnesium');
        
        // If magnesium is not in top 3, add it as 4th
        if (!hasMagnesium && mineralContent.magnesium) {
            const magnesiumEntry = mineralValues.find(m => m.key === 'magnesium');
            if (magnesiumEntry) {
                top3.push(magnesiumEntry);
            }
        }
        
        return top3;
    };

    // Product-specific taglines
    const getProductTagline = (productName) => {
        const taglines = {
            'ROI': 'Real minerals. Real difference.',
            'Vichy Catalan': 'Pressure made it perfect.',
            'Marzia': "More than hydration. It's composition.",
            'Gerolsteiner': 'Not just pure. Right.'
        };
        return taglines[productName] || '';
    };

    // Product-specific body functions (exactly 3 per product)
    const getProductBodyFunctions = (productName, lang) => {
        const functions = {
            'ROI': lang === 'no' 
                ? ['Søvnkvalitet', 'Nervesystem', 'Muskelavslapning']
                : ['Sleep Quality', 'Nervous System', 'Muscle Relaxation'],
            'Vichy Catalan': lang === 'no'
                ? ['Fordøyelse', 'Elektrolyttbalanse', 'pH-balanse']
                : ['Digestion', 'Electrolyte Balance', 'pH Balance'],
            'Gerolsteiner': lang === 'no'
                ? ['Benhelse', 'Muskelytelse', 'Energinivå']
                : ['Bone Health', 'Muscle Performance', 'Energy Levels'],
            'Marzia': lang === 'no'
                ? ['Benhelse', 'Nervesystem', 'Muskelkontraksjon']
                : ['Bone Health', 'Nervous System', 'Muscle Contraction']
        };
        return functions[productName] || [];
    };

    // Product-specific use cases (BRUKSOMRÅDER)
    const getProductUseCases = (productName, lang) => {
        const useCases = {
            'ROI': lang === 'no'
                ? ['Sport og trening', 'Kveldsmåltid', 'Mineralboost', 'Kraftige oster']
                : ['Sports & Training', 'Evening Meals', 'Mineral Boost', 'Strong Cheeses'],
            'Vichy Catalan': lang === 'no'
                ? ['Skalldyr og sjømat', 'Proteinrike og fete retter', 'Sauna', 'Sport og trening']
                : ['Shellfish & Seafood', 'Protein-rich & Fatty Dishes', 'Sauna', 'Sports & Training'],
            'Gerolsteiner': lang === 'no'
                ? ['Proteinrike og fete retter', 'Sport og trening', 'Hverdagsmåltider']
                : ['Protein-rich & Fatty Dishes', 'Sports & Training', 'Everyday Meals'],
            'Marzia': lang === 'no'
                ? ['Hvit fisk og skalldyr', 'Pastaretter', 'Salater', 'Vin']
                : ['White Fish & Shellfish', 'Pasta Dishes', 'Salads', 'Wine']
        };
        return useCases[productName] || [];
    };

    // Product-specific unique descriptions (flowing text)
    const getProductUniqueText = (productName, lang) => {
        const texts = {
            'ROI': lang === 'no'
                ? 'Verdens høyeste naturlige magnesiuminnhold. Mer enn 400 års dokumentert terapeutisk historie fra Rogaška Slatina. Ideelt for muskelrestitusjon og stressmestring.'
                : "World's highest natural magnesium content. More than 400 years of documented therapeutic history from Rogaška Slatina. Ideal for muscle recovery and stress management.",
            'Vichy Catalan': lang === 'no'
                ? 'Tappet direkte ved 60°C vulkansk kilde. Blant verdens høyeste bikarbonatinnhold. Svært rikt på elektrolytter som gjør det ideelt for hydrering etter sauna og hard trening.'
                : 'Bottled directly at 60°C volcanic source. Among the world\'s highest bicarbonate content. Very rich in electrolytes, making it ideal for hydration after sauna and intense training.',
            'Gerolsteiner': lang === 'no'
                ? 'Tysklands mest solgte premium mineralvann fra 250 millioner år gamle vulkanske bergarter. Optimal kalsium-magnesium ratio for maksimal absorpsjon. Allsidig mineralvann for mat og trening.'
                : 'Germany\'s best-selling premium mineral water from 250 million year old volcanic rocks. Optimal calcium-magnesium ratio for maximum absorption. Versatile mineral water for food and training.',
            'Marzia': lang === 'no'
                ? 'Sjeldent høyt innhold av mineraler i et vann som høstes uten kullsyre. Ideelt for de som ønsker mineralrikt vann som samtidig er lavt på natrium. Kåret til verdens beste stille-vann i 2025.'
                : 'Remarkably high mineral content in a still water. Ideal for those who want mineral-rich water that is also low in sodium. Awarded world\'s best still water in 2025.'
        };
        return texts[productName] || '';
    };

    const BrochurePage = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [progress, setProgress] = useState('');
    const { t, language } = useLanguage();

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await productAPI.getAll();
                setProducts(res.data);
            } catch (error) {
                console.error('Failed to fetch products:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, []);

    // Helper function to convert image URL to base64 via proxy with white background
    const loadImageAsBase64 = async (url) => {
        try {
            // Use backend image proxy to avoid CORS issues
            const API_URL = process.env.REACT_APP_BACKEND_URL || '';
            const proxyResponse = await fetch(`${API_URL}/api/image-proxy?url=${encodeURIComponent(url)}`);
            const proxyData = await proxyResponse.json();
            
            if (proxyData.success && proxyData.data) {
                // Convert to JPEG with white background to handle PNG transparency
                return new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        ctx.fillStyle = '#FFFFFF';
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                        ctx.drawImage(img, 0, 0);
                        resolve(canvas.toDataURL('image/jpeg', 0.9));
                    };
                    img.onerror = () => resolve(null);
                    img.src = proxyData.data;
                });
            }
            return null;
        } catch (error) {
            console.error('Failed to load image:', url, error);
            return null;
        }
    };

    // Pre-load all product images
    const preloadImages = async (products) => {
        const imagePromises = products.map(async (product) => {
            const base64 = await loadImageAsBase64(product.image);
            return { id: product.id, name: product.name, base64 };
        });
        return Promise.all(imagePromises);
    };

    const generatePDF = async () => {
        if (products.length < 4) {
            alert('Venter på produktdata...');
            return;
        }

        setGenerating(true);
        setProgress('Laster bilder...');

        try {
            // Pre-load all images
            const imageCache = {};
            for (let i = 0; i < products.length; i++) {
                const product = products[i];
                setProgress(`Laster bilde ${i + 1}/${products.length}: ${product.name}...`);
                const base64 = await loadImageAsBase64(product.image);
                if (base64) {
                    imageCache[product.name] = base64;
                }
            }

            setProgress('Genererer PDF...');

            // A4 dimensions in mm: 210 x 297
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });

            // A4 landscape: 297mm x 210mm
            const pageWidth = 297;
            const pageHeight = 210;
            const panelWidth = pageWidth / 3; // ~99mm per panel
            const margin = 8;

            // Colors
            const darkGray = [35, 35, 35];
            const mediumGray = [80, 80, 80];
            const lightGray = [150, 150, 150];
            const accentBlue = [52, 73, 94]; // Fjord color

            // ===== PAGE 1 (Outside of brochure) =====
            
            // --- Panel 1: Back cover with OM OSS + Contact info ---
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, panelWidth, pageHeight, 'F');
            
            // OM OSS section
            pdf.setTextColor(...darkGray);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(14);
            pdf.text(language === 'no' ? 'OM OSS' : 'ABOUT US', panelWidth / 2, 25, { align: 'center' });
            
            // Decorative line
            pdf.setDrawColor(...accentBlue);
            pdf.setLineWidth(0.5);
            pdf.line(panelWidth / 2 - 15, 30, panelWidth / 2 + 15, 30);
            
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(...mediumGray);
            
            const aboutTextNo = [
                'Raw Minerals er Norges første',
                'spesialiserte importør av',
                'premium mineralvann.',
                '',
                'Vann er mer enn bare H2O.',
                'Mineralitet og opprinnelsessted',
                'definerer smak, karakter og',
                'helsemessige fordeler.',
                '',
                'Vårt utvalg er nøye utvalgt',
                'fra Europas mest anerkjente',
                'kilder - hver flaske forteller',
                'en geologisk historie som',
                'spenner over tusenvis av år.',
                '',
                '—',
                '',
                '"Minerals matter.',
                'The rest is just liquid."'
            ];
            
            const aboutTextEn = [
                'Raw Minerals is Norway\'s first',
                'specialized importer of',
                'premium mineral water.',
                '',
                'Water is more than just H2O.',
                'Minerality and origin define',
                'taste, character and',
                'health benefits.',
                '',
                'Our selection is carefully',
                'chosen from Europe\'s most',
                'renowned sources - each bottle',
                'tells a geological story',
                'spanning thousands of years.',
                '',
                '—',
                '',
                '"Minerals matter.',
                'The rest is just liquid."'
            ];
            
            const aboutText = language === 'no' ? aboutTextNo : aboutTextEn;
            
            let aboutY = 42;
            aboutText.forEach(line => {
                if (line === '—') {
                    pdf.setDrawColor(...lightGray);
                    pdf.line(panelWidth / 2 - 10, aboutY, panelWidth / 2 + 10, aboutY);
                    aboutY += 5;
                } else if (line.startsWith('"')) {
                    pdf.setFont('helvetica', 'italic');
                    pdf.text(line, panelWidth / 2, aboutY, { align: 'center' });
                    pdf.setFont('helvetica', 'normal');
                    aboutY += 5;
                } else {
                    pdf.text(line, panelWidth / 2, aboutY, { align: 'center' });
                    aboutY += 5;
                }
            });
            
            // Contact info at bottom
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8);
            pdf.setTextColor(...darkGray);
            pdf.text('RAW MINERALS AS', panelWidth / 2, pageHeight - 45, { align: 'center' });
            
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(7);
            pdf.setTextColor(...lightGray);
            const contactLines = language === 'no' 
                ? ['Norges første spesialiserte importør', 'av mineralvann', '', 'www.rawminerals.no', 'hello@rawminerals.no']
                : ['Norway\'s first specialized importer', 'of mineral water', '', 'www.rawminerals.no', 'hello@rawminerals.no'];
            
            let contactY = pageHeight - 38;
            contactLines.forEach(line => {
                pdf.text(line, panelWidth / 2, contactY, { align: 'center' });
                contactY += 5;
            });

            // --- Panel 2: FRONT COVER ---
            pdf.setFillColor(...darkGray);
            pdf.rect(panelWidth, 0, panelWidth, pageHeight, 'F');
            
            // Main title
            pdf.setTextColor(255, 255, 255);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(28);
            pdf.text('RAW', panelWidth + panelWidth/2, pageHeight/2 - 15, { align: 'center' });
            pdf.text('MINERALS', panelWidth + panelWidth/2, pageHeight/2 + 5, { align: 'center' });
            
            // Slogan
            pdf.setFont('helvetica', 'italic');
            pdf.setFontSize(10);
            pdf.setTextColor(200, 200, 200);
            pdf.text('"water is just the carrier"', panelWidth + panelWidth/2, pageHeight/2 + 25, { align: 'center' });
            
            // Decorative line
            pdf.setDrawColor(255, 255, 255);
            pdf.setLineWidth(0.3);
            pdf.line(panelWidth + 20, pageHeight/2 + 35, panelWidth*2 - 20, pageHeight/2 + 35);
            
            // Footer text
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(7);
            pdf.setTextColor(150, 150, 150);
            pdf.text('Norge', panelWidth + panelWidth/2, pageHeight - 15, { align: 'center' });

            // --- Panel 3: ROI (full panel) ---
            const roi = products.find(p => p.name === 'ROI') || products[0];
            await drawProductPanelWithImage(pdf, roi, panelWidth * 2, margin, panelWidth, pageHeight, darkGray, mediumGray, lightGray, accentBlue, imageCache);

            // ===== PAGE 2 (Inside of brochure) =====
            pdf.addPage();
            
            // --- Panel 1: Vichy Catalan (full panel) ---
            const vichy = products.find(p => p.name === 'Vichy Catalan') || products[1];
            await drawProductPanelWithImage(pdf, vichy, 0, margin, panelWidth, pageHeight, darkGray, mediumGray, lightGray, accentBlue, imageCache);
            
            // --- Panel 2: Gerolsteiner (full panel) ---
            const gerolsteiner = products.find(p => p.name === 'Gerolsteiner') || products[2];
            await drawProductPanelWithImage(pdf, gerolsteiner, panelWidth, margin, panelWidth, pageHeight, darkGray, mediumGray, lightGray, accentBlue, imageCache);
            
            // --- Panel 3: Marzia (full panel) ---
            const marzia = products.find(p => p.name === 'Marzia') || products[3];
            await drawProductPanelWithImage(pdf, marzia, panelWidth * 2, margin, panelWidth, pageHeight, darkGray, mediumGray, lightGray, accentBlue, imageCache);

            setProgress('Lagrer PDF...');
            // Save the PDF
            pdf.save('RawMinerals_Brosjyre.pdf');
            
        } catch (error) {
            console.error('PDF generation error:', error);
            alert('Kunne ikke generere PDF. Prøv igjen.');
        } finally {
            setGenerating(false);
            setProgress('');
        }
    };

    // Draw a full product panel - STANDARDIZED LAYOUT
    // Name centered at top, text left, image right, "Unikt" centered at bottom
    const drawProductPanelWithImage = async (pdf, product, x, margin, panelWidth, pageHeight, darkGray, mediumGray, lightGray, accentBlue, imageCache) => {
        if (!product) return;
        
        const contentX = x + margin;
        const contentWidth = panelWidth - margin * 2;
        const halfWidth = contentWidth / 2 - 3;
        
        // Get localized content using new helper functions
        const productOrigin = getLocalizedText(product.origin, language);
        const bodyFunctions = getProductBodyFunctions(product.name, language);
        const useCases = getProductUseCases(product.name, language);
        const uniqueText = getProductUniqueText(product.name, language);
        const tagline = getProductTagline(product.name);
        
        // Background
        pdf.setFillColor(255, 255, 255);
        pdf.rect(x, 0, panelWidth, pageHeight, 'F');
        
        // === TOP: Product name centered (moved down for better centering) ===
        pdf.setTextColor(...darkGray);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(18);
        pdf.text(product.name.toUpperCase(), x + panelWidth / 2, 18, { align: 'center' });
        
        // Origin & Price centered below name
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(...lightGray);
        pdf.text(`${productOrigin} · ${product.bottle_size}`, x + panelWidth / 2, 25, { align: 'center' });
        
        // Decorative line
        pdf.setDrawColor(...accentBlue);
        pdf.setLineWidth(0.5);
        pdf.line(x + panelWidth / 2 - 20, 30, x + panelWidth / 2 + 20, 30);
        
        // === LEFT SIDE: Text content (moved down for centering) ===
        let leftY = 40;
        const leftX = contentX;
        
        // VANNPROFIL (with TDS)
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(...darkGray);
        pdf.text(language === 'no' ? 'VANNPROFIL' : 'WATER PROFILE', leftX, leftY);
        leftY += 5;
        
        const profile = product.mineral_profile;
        if (profile) {
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8.5);
            pdf.setTextColor(...mediumGray);
            const items = [
                { label: language === 'no' ? 'Mineralitet' : 'Minerality', value: getLocalizedText(profile.minerality, language) },
                { label: language === 'no' ? 'Hardhet' : 'Hardness', value: getLocalizedText(profile.hardness_level, language) },
                { label: language === 'no' ? 'Kullsyre' : 'Carbonation', value: getLocalizedText(profile.carbonation, language) },
                { label: 'TDS', value: product.mineral_content?.tds || '-' }
            ];
            items.forEach(item => {
                pdf.text(`${item.label}: ${item.value}`, leftX, leftY);
                leftY += 4;
            });
        }
        leftY += 4;
        
        // VIKTIGSTE MINERALER (Top 3 + magnesium)
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(...darkGray);
        pdf.text(language === 'no' ? 'VIKTIGSTE MINERALER' : 'KEY MINERALS', leftX, leftY);
        leftY += 5;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8.5);
        pdf.setTextColor(...mediumGray);
        
        const topMinerals = getTopMinerals(product.mineral_content);
        const mineralLabels = language === 'no' 
            ? { calcium: 'Kalsium', magnesium: 'Magnesium', sodium: 'Natrium', bicarbonate: 'Bikarbonat', potassium: 'Kalium', sulfate: 'Sulfat', chloride: 'Klorid', silica: 'Silika' }
            : { calcium: 'Calcium', magnesium: 'Magnesium', sodium: 'Sodium', bicarbonate: 'Bicarbonate', potassium: 'Potassium', sulfate: 'Sulfate', chloride: 'Chloride', silica: 'Silica' };
        
        topMinerals.forEach(m => {
            pdf.text(`${mineralLabels[m.key]}: ${m.value}`, leftX, leftY);
            leftY += 4;
        });
        leftY += 4;
        
        // KROPPSFUNKSJONER (exactly 3 per product)
        if (bodyFunctions.length > 0) {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(9);
            pdf.setTextColor(...darkGray);
            pdf.text(language === 'no' ? 'KROPPSFUNKSJONER' : 'BODY FUNCTIONS', leftX, leftY);
            leftY += 5;
            
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(...mediumGray);
            bodyFunctions.forEach(func => {
                pdf.text(`• ${func}`, leftX, leftY);
                leftY += 4;
            });
            leftY += 4;
        }
        
        // BRUKSOMRÅDER (previously PASSER TIL)
        if (useCases.length > 0) {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(9);
            pdf.setTextColor(...darkGray);
            pdf.text(language === 'no' ? 'BRUKSOMRÅDER' : 'USE CASES', leftX, leftY);
            leftY += 5;
            
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(...mediumGray);
            useCases.forEach(useCase => {
                pdf.text(`• ${useCase}`, leftX, leftY);
                leftY += 4;
            });
        }
        
        // === RIGHT SIDE: Product image (moved down for centering) ===
        const imgBase64 = imageCache[product.name];
        if (imgBase64) {
            try {
                // Calculate proper aspect ratio
                const img = new Image();
                await new Promise((resolve) => {
                    img.onload = resolve;
                    img.onerror = resolve;
                    img.src = imgBase64;
                });
                
                const aspectRatio = img.width / img.height;
                const maxWidth = halfWidth;
                const maxHeight = 70;
                
                let imgWidth, imgHeight;
                
                // Maintain aspect ratio - fit within bounds
                if (aspectRatio > (maxWidth / maxHeight)) {
                    // Image is wider than space
                    imgWidth = maxWidth;
                    imgHeight = maxWidth / aspectRatio;
                } else {
                    // Image is taller than space
                    imgHeight = maxHeight;
                    imgWidth = maxHeight * aspectRatio;
                }
                
                const imgX = contentX + halfWidth + 6 + (maxWidth - imgWidth) / 2;
                const imgY = 38 + (maxHeight - imgHeight) / 2;  // Moved down 10mm
                pdf.addImage(imgBase64, 'JPEG', imgX, imgY, imgWidth, imgHeight, undefined, 'MEDIUM');
            } catch (e) {
                console.error('Error adding image:', e);
            }
        }
        
        // === BOTTOM: "Hva gjør vannet unikt" - flowing text, larger font ===
        if (uniqueText) {
            const bottomY = pageHeight - 50;
            
            pdf.setDrawColor(...accentBlue);
            pdf.setLineWidth(0.3);
            pdf.line(x + panelWidth / 2 - 15, bottomY - 3, x + panelWidth / 2 + 15, bottomY - 3);
            
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8);
            pdf.setTextColor(...darkGray);
            pdf.text(language === 'no' ? 'HVA GJØR DETTE VANNET UNIKT?' : 'WHAT MAKES THIS WATER UNIQUE?', x + panelWidth / 2, bottomY, { align: 'center' });
            
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(...mediumGray);
            
            // Split text to fit panel width
            const lines = pdf.splitTextToSize(uniqueText, contentWidth - 4);
            let uniqueY = bottomY + 5;
            lines.slice(0, 4).forEach(line => {
                pdf.text(line, x + panelWidth / 2, uniqueY, { align: 'center' });
                uniqueY += 4.5;
            });
        }
        
        // === TAGLINE at very bottom ===
        if (tagline) {
            pdf.setFont('helvetica', 'italic');
            pdf.setFontSize(9);
            pdf.setTextColor(...accentBlue);
            pdf.text(`"${tagline}"`, x + panelWidth / 2, pageHeight - 8, { align: 'center' });
        }
    };

    // Draw a mini product panel - SAME LAYOUT AS ROI (for half-height panels)
    // Name centered at top, text left, image right, "Unikt" centered at bottom
    const drawProductMiniWithImage = async (pdf, product, panelX, panelY, panelWidth, panelHeight, margin, darkGray, mediumGray, lightGray, imageCache) => {
        if (!product) return;
        
        const contentX = panelX + margin;
        const contentWidth = panelWidth - margin * 2;
        const halfWidth = contentWidth / 2 - 3;
        const accentBlue = [52, 73, 94];
        
        // Get localized content using new helper functions
        const productOrigin = getLocalizedText(product.origin, language);
        const bodyFunctions = getProductBodyFunctions(product.name, language);
        const useCases = getProductUseCases(product.name, language);
        const uniqueText = getProductUniqueText(product.name, language);
        const tagline = getProductTagline(product.name);
        
        // === TOP: Product name centered ===
        pdf.setTextColor(...darkGray);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.text(product.name.toUpperCase(), panelX + panelWidth / 2, panelY + 8, { align: 'center' });
        
        // Origin & Price centered below name
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6);
        pdf.setTextColor(...lightGray);
        pdf.text(`${productOrigin} · ${product.bottle_size}`, panelX + panelWidth / 2, panelY + 13, { align: 'center' });
        
        // Decorative line
        pdf.setDrawColor(...accentBlue);
        pdf.setLineWidth(0.5);
        pdf.line(panelX + panelWidth / 2 - 15, panelY + 16, panelX + panelWidth / 2 + 15, panelY + 16);
        
        // === LEFT SIDE: Text content ===
        let leftY = panelY + 22;
        const leftX = contentX;
        
        // VANNPROFIL (with TDS)
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(5.5);
        pdf.setTextColor(...darkGray);
        pdf.text(language === 'no' ? 'VANNPROFIL' : 'WATER PROFILE', leftX, leftY);
        leftY += 3.5;
        
        const profile = product.mineral_profile;
        if (profile) {
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(5);
            pdf.setTextColor(...mediumGray);
            const items = [
                { label: language === 'no' ? 'Mineralitet' : 'Minerality', value: getLocalizedText(profile.minerality, language) },
                { label: language === 'no' ? 'Hardhet' : 'Hardness', value: getLocalizedText(profile.hardness_level, language) },
                { label: language === 'no' ? 'Kullsyre' : 'Carbonation', value: getLocalizedText(profile.carbonation, language) },
                { label: 'TDS', value: product.mineral_content?.tds || '-' }
            ];
            items.forEach(item => {
                pdf.text(`${item.label}: ${item.value}`, leftX, leftY);
                leftY += 3;
            });
        }
        leftY += 2;
        
        // VIKTIGSTE MINERALER
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(5.5);
        pdf.setTextColor(...darkGray);
        pdf.text(language === 'no' ? 'VIKTIGSTE MINERALER' : 'KEY MINERALS', leftX, leftY);
        leftY += 3.5;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(5);
        pdf.setTextColor(...mediumGray);
        
        const topMinerals = getTopMinerals(product.mineral_content);
        const mineralLabels = language === 'no' 
            ? { calcium: 'Kalsium', magnesium: 'Magnesium', sodium: 'Natrium', bicarbonate: 'Bikarbonat', potassium: 'Kalium', sulfate: 'Sulfat', chloride: 'Klorid', silica: 'Silika' }
            : { calcium: 'Calcium', magnesium: 'Magnesium', sodium: 'Sodium', bicarbonate: 'Bicarbonate', potassium: 'Potassium', sulfate: 'Sulfate', chloride: 'Chloride', silica: 'Silica' };
        
        topMinerals.forEach(m => {
            pdf.text(`${mineralLabels[m.key]}: ${m.value}`, leftX, leftY);
            leftY += 3;
        });
        leftY += 2;
        
        // KROPPSFUNKSJONER (exactly 3)
        if (bodyFunctions.length > 0) {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(5.5);
            pdf.setTextColor(...darkGray);
            pdf.text(language === 'no' ? 'KROPPSFUNKSJONER' : 'BODY FUNCTIONS', leftX, leftY);
            leftY += 3.5;
            
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(4.5);
            pdf.setTextColor(...mediumGray);
            bodyFunctions.forEach(func => {
                pdf.text(`• ${func}`, leftX, leftY);
                leftY += 3;
            });
            leftY += 2;
        }
        
        // BRUKSOMRÅDER
        if (useCases.length > 0) {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(5.5);
            pdf.setTextColor(...darkGray);
            pdf.text(language === 'no' ? 'BRUKSOMRÅDER' : 'USE CASES', leftX, leftY);
            leftY += 3.5;
            
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(4.5);
            pdf.setTextColor(...mediumGray);
            useCases.slice(0, 2).forEach(useCase => {
                pdf.text(`• ${useCase}`, leftX, leftY);
                leftY += 3;
            });
        }
        
        // === RIGHT SIDE: Product image ===
        const imgBase64 = imageCache[product.name];
        if (imgBase64) {
            try {
                const imgX = contentX + halfWidth + 5;
                const imgY = panelY + 20;
                const imgWidth = halfWidth - 2;
                const imgHeight = 45;
                pdf.addImage(imgBase64, 'JPEG', imgX, imgY, imgWidth, imgHeight, undefined, 'MEDIUM');
            } catch (e) {
                console.error('Error adding mini image:', e);
            }
        }
        
        // === BOTTOM: "Hva gjør vannet unikt" - flowing text ===
        if (uniqueText) {
            const bottomY = panelY + panelHeight - 22;
            
            pdf.setDrawColor(...accentBlue);
            pdf.setLineWidth(0.3);
            pdf.line(panelX + panelWidth / 2 - 12, bottomY - 2, panelX + panelWidth / 2 + 12, bottomY - 2);
            
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(5.5);
            pdf.setTextColor(...darkGray);
            pdf.text(language === 'no' ? 'HVA GJØR DETTE VANNET UNIKT?' : 'WHAT MAKES THIS WATER UNIQUE?', panelX + panelWidth / 2, bottomY, { align: 'center' });
            
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(5);
            pdf.setTextColor(...mediumGray);
            
            const lines = pdf.splitTextToSize(uniqueText, contentWidth - 4);
            let uniqueY = bottomY + 4;
            lines.slice(0, 2).forEach(line => {
                pdf.text(line, panelX + panelWidth / 2, uniqueY, { align: 'center' });
                uniqueY += 3.5;
            });
        }
        
        // === TAGLINE at very bottom ===
        if (tagline) {
            pdf.setFont('helvetica', 'italic');
            pdf.setFontSize(5);
            pdf.setTextColor(...accentBlue);
            pdf.text(`"${tagline}"`, panelX + panelWidth / 2, panelY + panelHeight - 4, { align: 'center' });
        }
    };

    return (
        <div className="min-h-screen bg-stone-50 py-20" data-testid="brochure-page">
            <SEO 
                title="Brosjyre"
                description="Last ned vår profesjonelle A4-brosjyre med produktinformasjon om ROI, Vichy Catalan, Gerolsteiner og Marzia mineralvann."
                canonical="/brochure"
            />
            <div className="max-w-4xl mx-auto px-6">
                {/* Header */}
                <div className="text-center mb-16">
                    <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-4">
                        Markedsmateriell
                    </p>
                    <h1 className="font-display text-4xl md:text-5xl font-light mb-6">
                        Last ned brosjyre
                    </h1>
                    <p className="text-stone-600 max-w-xl mx-auto">
                        En profesjonell A4-brosjyre med produktbilder, klar for print. Brettes til 6 sider med all informasjon om våre premium mineralvann.
                    </p>
                </div>

                {/* Preview Card */}
                <div className="bg-white border border-stone-200 rounded-lg overflow-hidden mb-8">
                    {/* Brochure Preview */}
                    <div className="p-8 border-b border-stone-100">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-6">
                            Forhåndsvisning
                        </h3>
                        
                        {/* Outside of brochure */}
                        <div className="mb-8">
                            <p className="text-xs text-stone-400 mb-3">Utside (når brettet)</p>
                            <div className="grid grid-cols-3 gap-1 h-48 border border-stone-200">
                                {/* Back cover */}
                                <div className="bg-stone-50 flex items-center justify-center p-4">
                                    <div className="text-center">
                                        <p className="text-[10px] text-stone-400">Bakside</p>
                                        <p className="text-[8px] text-stone-300 mt-1">Kontaktinfo</p>
                                    </div>
                                </div>
                                {/* Front cover */}
                                <div className="bg-stone-800 flex items-center justify-center p-4">
                                    <div className="text-center text-white">
                                        <p className="font-bold text-sm">RAW</p>
                                        <p className="font-bold text-sm">MINERALS</p>
                                        <p className="text-[8px] italic text-stone-300 mt-2">"water is just the carrier"</p>
                                    </div>
                                </div>
                                {/* About fold-in */}
                                <div className="bg-white flex items-center justify-center p-4 border-l border-stone-200">
                                    <div className="text-center">
                                        <p className="text-xs font-semibold text-stone-700">OM OSS</p>
                                        <p className="text-[8px] text-stone-400 mt-1">Vår historie</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Inside of brochure */}
                        <div>
                            <p className="text-xs text-stone-400 mb-3">Innside (når åpnet) - med produktbilder</p>
                            <div className="grid grid-cols-3 gap-1 h-56 border border-stone-200">
                                {/* Product 1 - ROI */}
                                <div className="bg-white flex flex-col items-center justify-center p-3 border-r border-stone-100">
                                    <div className="w-12 h-16 bg-stone-100 rounded mb-2 flex items-center justify-center">
                                        <ImageIcon className="w-6 h-6 text-stone-300" />
                                    </div>
                                    <p className="text-xs font-bold text-stone-700">ROI</p>
                                    <p className="text-[8px] text-stone-400">Slovenia</p>
                                </div>
                                {/* Products 2 & 3 */}
                                <div className="bg-stone-50 flex flex-col">
                                    <div className="flex-1 flex items-center p-2 border-b border-stone-200">
                                        <div className="w-8 h-10 bg-stone-200 rounded mr-2 flex items-center justify-center">
                                            <ImageIcon className="w-4 h-4 text-stone-400" />
                                        </div>
                                        <div>
                                            <p className="text-[9px] font-bold text-stone-700">VICHY CATALAN</p>
                                            <p className="text-[7px] text-stone-400">Spania</p>
                                        </div>
                                    </div>
                                    <div className="flex-1 flex items-center p-2">
                                        <div className="w-8 h-10 bg-stone-100 rounded mr-2 flex items-center justify-center">
                                            <ImageIcon className="w-4 h-4 text-stone-300" />
                                        </div>
                                        <div>
                                            <p className="text-[9px] font-bold text-stone-700">GEROLSTEINER</p>
                                            <p className="text-[7px] text-stone-400">Tyskland</p>
                                        </div>
                                    </div>
                                </div>
                                {/* Product 4 - Marzia */}
                                <div className="bg-white flex flex-col items-center justify-center p-3 border-l border-stone-100">
                                    <div className="w-12 h-16 bg-stone-100 rounded mb-2 flex items-center justify-center">
                                        <ImageIcon className="w-6 h-6 text-stone-300" />
                                    </div>
                                    <p className="text-xs font-bold text-stone-700">MARZIA</p>
                                    <p className="text-[8px] text-stone-400">Italia</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Info */}
                    <div className="p-6 bg-stone-50">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                            <div>
                                <p className="font-semibold text-stone-700 mb-1">Format</p>
                                <p className="text-stone-500">A4 Landskap (297 × 210 mm)</p>
                            </div>
                            <div>
                                <p className="font-semibold text-stone-700 mb-1">Bretting</p>
                                <p className="text-stone-500">Tri-fold (6 paneler)</p>
                            </div>
                            <div>
                                <p className="font-semibold text-stone-700 mb-1">Innhold</p>
                                <p className="text-stone-500">Forside, Om oss, 4 produkter med bilder</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Download Button */}
                <div className="text-center">
                    <Button
                        onClick={generatePDF}
                        disabled={loading || generating || products.length < 4}
                        className="bg-stone-800 hover:bg-stone-900 text-white rounded-none px-12 py-6 text-sm uppercase tracking-wider"
                        data-testid="download-brochure-btn"
                    >
                        {generating ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-3 animate-spin" />
                                {progress || 'Genererer PDF...'}
                            </>
                        ) : loading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-3 animate-spin" />
                                Laster produkter...
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4 mr-3" />
                                Last ned PDF med bilder
                            </>
                        )}
                    </Button>
                    
                    <p className="text-xs text-stone-400 mt-4">
                        PDF-filen genereres automatisk med produktbilder og oppdatert informasjon
                    </p>
                </div>

                {/* Instructions */}
                <div className="mt-16 border-t border-stone-200 pt-12">
                    <h3 className="text-lg font-semibold mb-6">Bruksanvisning for print</h3>
                    <ol className="space-y-4 text-stone-600">
                        <li className="flex gap-4">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-stone-800 text-white text-xs flex items-center justify-center">1</span>
                            <span>Last ned PDF-filen til din datamaskin</span>
                        </li>
                        <li className="flex gap-4">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-stone-800 text-white text-xs flex items-center justify-center">2</span>
                            <span>Åpne filen og velg "Skriv ut" (Ctrl+P / Cmd+P)</span>
                        </li>
                        <li className="flex gap-4">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-stone-800 text-white text-xs flex items-center justify-center">3</span>
                            <span>Velg "Tosidig utskrift" (dupleks) - snu på kort side</span>
                        </li>
                        <li className="flex gap-4">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-stone-800 text-white text-xs flex items-center justify-center">4</span>
                            <span>Brett arket i tre like deler som en Z-fold</span>
                        </li>
                    </ol>
                    
                    <div className="mt-8 p-4 bg-stone-100 rounded-lg">
                        <p className="text-sm text-stone-600">
                            <strong>Tips:</strong> For best resultat, bruk høykvalitets papir (minst 120 g/m²) og velg "Høy kvalitet" i utskriftsinnstillingene.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BrochurePage;
